import { observable, computed, action } from 'mobx';

// Utilities
import {
  initAuthentication,
  setAuthentication,
  deleteAuthentication
} from '../utilities/auth';
import { identify, track } from 'utilities/woopra';

// Services
import { login } from '../services/auth';
import {
  IMeInitializeResponseMe,
  IMeInitializeResponseNetwork,
  meInitialize,
  meEdit,
  meUploadPhoto,
  meDeletePhoto,
  meChangePassword
} from '../services/user';
import { ping, IGetPingResponse } from '../services/push';

// Types
export type IMeStore = MeStore;
export type IMe = IMeInitializeResponseMe;
export type IUserNetwork = IMeInitializeResponseNetwork;
export type INotificationCount = IGetPingResponse;

// Store
class MeStore {
  @observable userId?: string | null = null;
  @observable authentication: string | null = initAuthentication();
  @observable isAuthenticated: boolean = false;

  @observable user: IMe | null = null;
  @observable.shallow networks: IUserNetwork[] | null = null;

  // Detect GoNative
  isGoNative: boolean = navigator.userAgent.indexOf('gonative') > -1;
  isMobile: boolean = this.isGoNative;

  fetchTimer: NodeJS.Timer | number | null = null;
  @observable notificationCount: INotificationCount = { notificationCount: 0 };

  @computed
  get hasToken() {
    return this.authentication !== null;
  }

  @action
  async meInitialize() {
    const { networks, user } = await meInitialize();
    this.networks = networks;
    this.user = user;
    this.goNativeRequest(user.userId);
    identify({
      id: user.userId,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
      avatar: user.photoUrl || undefined
    });
    track();
  }

  @action
  async meEdit(values: {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
  }) {
    this.user = await meEdit({ body: values });
  }

  @action
  async meUploadPhoto(base64: string) {
    const response = await meUploadPhoto({ body: { base64 } });
    if (this.user) {
      this.user.photoUrl = response.photoUrl;
    }
  }

  @action
  async meDeletePhoto() {
    await meDeletePhoto();
    if (this.user) {
      this.user.photoUrl = null;
    }
  }

  @action
  async meChangePassword(values: { oldPassword: string; password: string }) {
    await meChangePassword({ body: values });
  }

  @action
  async login({
    username,
    password,
    remember
  }: {
    username: string;
    password: string;
    remember: boolean;
  }) {
    const { userId, accessToken } = await login({
      body: { username, password }
    });
    setAuthentication(accessToken, remember);
    if (this.authentication !== accessToken) {
      this.authentication = accessToken;
    }
    this.userId = userId;
    this.isAuthenticated = true;
  }

  goNativeRequest(userId: string) {
    if (this.isGoNative) {
      window.location.href =
        'gonative://registration/send?customData=' +
        encodeURIComponent(
          JSON.stringify({
            accessToken: this.authentication,
            userId
          })
        );
    }
  }

  @action
  async logout() {
    this.userId = null;
    this.user = null;
    this.authentication = null;
    this.isAuthenticated = false;
    this.networks = null;
    deleteAuthentication();
  }

  @action
  async updateLastNetworkId(networkId: string) {
    if (this.user !== null) {
      this.user.lastNetworkId = networkId;
    }
  }

  // Push
  async updateNotificationCount() {
    const res = await ping();
    if (this.notificationCount.notificationCount !== res.notificationCount) {
      this.notificationCount = res;
    }
  }

  async startNotificationCountPush() {
    await this.updateNotificationCount();
    this.fetchTimer = setTimeout(() => {
      this.startNotificationCountPush();
    }, 20000);
  }

  async stopNotificationCountPush() {
    // tslint:disable-next-line
    clearTimeout(this.fetchTimer as any);
  }
}

export default MeStore;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/store/MeStore.ts